"use client";
import { useEffect, useState } from "react";
import { Loader2, Paperclip, Send } from "lucide-react";
import { AnimatePresence, motion, MotionConfig } from "motion/react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useChat } from "@/features/chat/hooks/useChat";
import { ConversationView } from "@/features/chat/components/ConversationView";
import { ReportDialog } from "@/features/chat/components/ReportDialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { EmojiPickerButton } from "@/features/chat/components/EmojiPickerButton";
import { FilePreview } from "@/features/chat/components/FilePreview";
import GenericLoader from "@/components/GenericLoader.tsx";
import { TypingIndicator } from "@/features/chat/components/TypingIndicator.tsx";
import { RateLimitIndicator } from "@/features/chat/components/RateLimitIndicator";
import { DragDropOverlay } from "@/features/chat/components/DragDropOverlay.tsx";
import { StorageUsageIndicator } from "@/features/chat/components/StorageUsageIndicator";
import { WorkspaceBlockingIndicator } from "@/features/chat/components/WorkspaceBlockingIndicator";

export function WorkspaceChat() {
  // Use the chat hook
  const { conversationId } = useParams();
  const {
    // State
    messageInput,
    activeConversation,
    currentUser,
    messages,
    isLoading,
    selectedFiles,
    selectedFilesCount,
    isSending,
    editingMessageId,
    editingPendingMessageId,
    editMessageContent,
    animatedMessageIds,
    typingUsers,
    rateLimitState,

    // Refs
    editTextareaRef,
    scrollAreaRef,
    inputRef,
    fileInputRef,

    // Functions
    openConversation,
    sendMessage,
    setEditMessageContent,

    startEditingMessage,
    startEditingPendingMessage,
    cancelEditing,
    saveEditedMessage,
    deleteMessage,
    removePendingMessage,
    handleKeyPress,
    handleEditKeyPress,
    handleInputChange,
    handleFileChange,
    removeFile,
    handleAttachmentClick,
    isCurrentUser,
    formatFileSize,
    activeChannel,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    scrollToBottom,
    checkIfNearBottom,
    insertEmoji,
    hasScrolledToBottomRef,
    isInitialLoadRef,
    isAdjustingScrollRef,
    canSendMessage,
    getWorkspaceBlockingStatus,
    isDragging,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handlePaste,
  } = useChat(conversationId ?? "");

  const MAX_FILE_COUNT = 5;
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const { t } = useTranslation(["common", "chat"]);

  const debouncedOpenConversation = useDebouncedCallback(openConversation, 500);

  useEffect(() => {
    if (conversationId) {
      debouncedOpenConversation(conversationId);
    }
  }, [conversationId]);

  // Force scroll to bottom when component mounts and conversation is loaded
  useEffect(() => {
    if (messages && activeConversation && scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector(
        "[data-radix-scroll-area-viewport]",
      );

      if (scrollContainer) {
        // Use requestAnimationFrame to ensure DOM is fully rendered
        requestAnimationFrame(() => {
          scrollContainer.scrollTop = scrollContainer.scrollHeight;

          // Double check after a small delay to ensure all content is loaded
          setTimeout(() => {
            scrollContainer.scrollTop = scrollContainer.scrollHeight;
          }, 100);
          hasScrolledToBottomRef.current = true;
          if (isInitialLoadRef) isInitialLoadRef.current = false;
          if (isAdjustingScrollRef) isAdjustingScrollRef.current = false;
        });
      }
    }
  }, [activeConversation, scrollAreaRef]);

  const handleCloseReportDialog = () => {
    setReportDialogOpen(false);
  };

  if (!currentUser) return null;
  if (isLoading) {
    return <GenericLoader />;
  }

  return (
    <MotionConfig transition={{ type: "spring", stiffness: 300, damping: 30 }}>
      <motion.div
        animate={{ opacity: 1, y: 0 }}
        className="bg-background relative mx-auto flex h-[calc(100dvh-4rem)] w-full flex-col overflow-hidden rounded-lg md:w-2/3"
        initial={{ opacity: 0, y: 10 }}
        transition={{ duration: 0.3, type: "spring", stiffness: 300, damping: 30 }}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <DragDropOverlay isDragging={isDragging} />
        <div className="relative flex h-full flex-col">
          {/* Conversation View */}
          {activeConversation && (
            <ConversationView
              activeConversation={activeConversation}
              animatedMessageIds={animatedMessageIds}
              cancelEditing={cancelEditing}
              checkIfNearBottom={checkIfNearBottom}
              currentUser={currentUser}
              deleteMessage={deleteMessage}
              disableMessageActions={canSendMessage().isWorkspaceBlocked}
              editMessageContent={editMessageContent}
              editTextareaRef={editTextareaRef}
              editingMessageId={editingMessageId}
              editingPendingMessageId={editingPendingMessageId}
              fetchNextPage={fetchNextPage}
              formatFileSize={formatFileSize}
              handleEditKeyPress={handleEditKeyPress}
              hasNextPage={hasNextPage}
              isCurrentUser={isCurrentUser}
              isFetchingNextPage={isFetchingNextPage}
              isLoading={isLoading}
              messages={messages}
              mode="full"
              removePendingMessage={removePendingMessage}
              saveEditedMessage={saveEditedMessage}
              scrollAreaRef={scrollAreaRef}
              scrollToBottom={scrollToBottom}
              setEditMessageContent={setEditMessageContent}
              startEditingMessage={startEditingMessage}
              startEditingPendingMessage={startEditingPendingMessage}
            />
          )}

          {/* Rate limit indicator positioned above typing indicator */}
          <AnimatePresence>
            {(() => {
              const sendStatus = canSendMessage();

              // Only show rate limit indicator if not workspace blocked
              if (!sendStatus.isWorkspaceBlocked) {
                return (
                  <RateLimitIndicator
                    canSend={sendStatus.allowed}
                    className="mx-2"
                    cooldownEnd={rateLimitState.cooldownEnd}
                    level={rateLimitState.level}
                    remainingCooldown={rateLimitState.remainingCooldown}
                    timeUntilNextMessage={sendStatus.timeRemaining}
                  />
                );
              }

              return null;
            })()}
          </AnimatePresence>

          {/* Workspace blocking indicator */}
          <AnimatePresence>
            {(() => {
              const blockingStatus = getWorkspaceBlockingStatus();

              return (
                <WorkspaceBlockingIndicator
                  className="mx-2"
                  conversationType={activeConversation?.type || "individual"}
                  hasLeft={blockingStatus.hasLeft}
                  isBlocked={blockingStatus.isBlocked}
                />
              );
            })()}
          </AnimatePresence>

          {/* Typing indicator positioned above input */}
          <AnimatePresence>
            <TypingIndicator users={typingUsers} />
          </AnimatePresence>

          {activeConversation && (
            <div className="p-2 pt-1 pb-4">
              {/* Selected files preview */}
              <AnimatePresence>
                {selectedFilesCount > 0 && (
                  <motion.div
                    animate={{ opacity: 1, height: "auto", y: 0 }}
                    className="mb-2 overflow-hidden"
                    exit={{ opacity: 0, height: 0, y: -5 }}
                    initial={{ opacity: 0, height: 0, y: -5 }}
                  >
                    <div className="flex flex-wrap gap-2">
                      {selectedFiles.map((fileData, index) => (
                        <FilePreview
                          key={index}
                          file={fileData.file}
                          index={index}
                          isValid={fileData.isValid}
                          validationError={fileData.validationError}
                          onRemove={removeFile}
                        />
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Only render input area if user can send messages */}
              {!canSendMessage().isWorkspaceBlocked && (
                <div className="bg-side-accent flex gap-2 rounded-lg p-2 p-3 shadow-inner transition-all focus-within:ring-2 focus-within:ring-gray-600 sm:p-3">
                  {/* Hidden file input */}
                  <input
                    ref={fileInputRef}
                    multiple
                    className="hidden"
                    type="file"
                    onChange={handleFileChange}
                  />

                  {/* Attachment button */}
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          className="text-muted-foreground hover:text-accent-foreground hover:bg-side-accent/20 flex h-10 w-6 w-8 items-center rounded-full bg-transparent p-0 sm:w-8"
                          disabled={
                            !activeChannel ||
                            isLoading ||
                            selectedFilesCount >= MAX_FILE_COUNT
                          }
                          type="button"
                          variant="ghost"
                          onClick={handleAttachmentClick}
                        >
                          <Paperclip className="h-3 h-4 w-3 w-4 sm:h-4 sm:w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="top">{t("chat:attachments.attachFile")}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  <Input
                    ref={inputRef}
                    className="placeholder:text-muted-foreground flex-1 border-none bg-transparent text-white focus-visible:ring-0 focus-visible:ring-offset-0"
                    disabled={!activeChannel || isLoading}
                    placeholder={
                      activeChannel()
                        ? isSending
                          ? t("chat:input.sendingPlaceholder")
                          : t("chat:input.typingPlaceholder")
                        : t("chat:input.connectingPlaceholder")
                    }
                    value={messageInput}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyPress}
                    onPaste={handlePaste}
                  />

                  {/* Add the EmojiPickerButton here, before the send button */}
                  <EmojiPickerButton
                    buttonClassName="flex h-10 w-6 w-8 items-center rounded-full bg-transparent p-0 sm:w-8"
                    onEmojiSelect={insertEmoji}
                  />

                  <Button
                    className="bg-transparent text-gray-400 transition-colors hover:bg-[#444654]"
                    disabled={
                      (!messageInput.trim() && selectedFilesCount === 0) ||
                      isLoading
                    }
                    type="submit"
                    onClick={sendMessage}
                  >
                    {isSending || isLoading ? (
                      <Loader2 className="h-4 h-5 w-4 w-5 animate-spin sm:h-5 sm:w-5" />
                    ) : (
                      <Send className="h-4 h-5 w-4 w-5 sm:h-5 sm:w-5" />
                    )}
                    <span className="sr-only">Send</span>
                  </Button>
                </div>
              )}

              {/* File upload restrictions info and storage usage */}
              <div className="text-muted-foreground relative mt-1.5 flex flex-col items-center justify-center gap-1 text-center text-[11px]">
                {activeConversation?.storage_usage && (
                  <StorageUsageIndicator storageUsage={activeConversation.storage_usage} />
                )}
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Report Dialog */}
      <ReportDialog
        conversationId={activeConversation?.id}
        conversationName={activeConversation?.name}
        isOpen={reportDialogOpen}
        onClose={handleCloseReportDialog}
      />
    </MotionConfig>
  );
}
